import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";

const EFFECTIVE_DATE = "May 27, 2026";

const sections = [
  {
    title: "Acceptance Of Terms",
    body: (
      <>
        By accessing or using Impact Legacy Group services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must discontinue use of our services immediately.
      </>
    ),
  },
  {
    title: "Services Provided",
    body: (
      <>
        Impact Legacy Group acts as an insurance broker to help customers find and purchase life insurance policies. We provide quotes, submit applications to insurance carriers on your behalf, and assist with the policy acquisition process.
      </>
    ),
  },
  {
    title: "Use Of Our Services",
    body: (
      <>
        When using our services, you agree to:
        <ul className="list-none mt-4 space-y-2 pl-0">
          <li><span className="text-[#D4AF37]">·</span> Provide accurate and truthful information when requesting quotes or completing applications.</li>
          <li><span className="text-[#D4AF37]">·</span> Use our services only for lawful purposes.</li>
          <li><span className="text-[#D4AF37]">·</span> Not attempt to misuse or interfere with our website or services.</li>
        </ul>
      </>
    ),
  },
  {
    title: "No Guarantee Of Coverage",
    body: (
      <>
        Impact Legacy Group does not guarantee that you will be approved for life insurance coverage or that the quotes provided will match the final policy cost. Approval and pricing are determined by the insurance carrier based on their underwriting guidelines.
      </>
    ),
  },
  {
    title: "Intellectual Property",
    body: (
      <>
        All content, design, logos, and materials on our website are the property of Impact Legacy Group and are protected by copyright and trademark laws. You may not copy, reproduce, distribute, or use any of our intellectual property without prior written consent.
      </>
    ),
  },
  {
    title: "SMS Text Messaging",
    body: (
      <>
        Impact Legacy Group will send you meeting reminders and promotional texts after you give consent to text you.
        <p className="mt-4">
          You can cancel the SMS service at any time. Just text <span className="text-white">"STOP"</span> to the short code. After you send the SMS message "STOP" to us, we will send you an SMS message to confirm that you have unsubscribed. After this you will no longer receive SMS messages from us. If you want to join again, just sign up as you did the first time and we will start sending SMS messages to you again.
        </p>
        <p className="mt-4">
          If you are experiencing issues with the messaging program you can reply with the keyword <span className="text-white">HELP</span> for more assistance, or you can get help directly at <a href="mailto:impactlegacy.davis@gmail.com" className="text-[#D4AF37] hover:underline">impactlegacy.davis@gmail.com</a> or <a href="tel:9364435365" className="text-[#D4AF37] hover:underline">936-443-5365</a>. Carriers are not liable for delayed or undelivered messages.
        </p>
        <p className="mt-4">
          As always, message and data rates may apply for any messages sent to you from us and to us from you. Message frequency varies — you may receive up to 10 messages per month. If you have any questions about your text plan or data plan, it is best to contact your wireless provider. If you have any questions regarding privacy, please read our <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>.
        </p>
      </>
    ),
  },
  {
    title: "Privacy Policy",
    body: (
      <>
        Your use of our services is also governed by our <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>, which outlines how we collect, use, and protect your information. By agreeing to these Terms and Conditions, you acknowledge that you have read and understood our Privacy Policy.
      </>
    ),
  },
  {
    title: "Limitation Of Liability",
    body: (
      <>
        Impact Legacy Group is not responsible for any loss or damage arising from:
        <ul className="list-none mt-4 space-y-2 pl-0">
          <li><span className="text-[#D4AF37]">·</span> The use or inability to use our services.</li>
          <li><span className="text-[#D4AF37]">·</span> Errors or inaccuracies in the quotes or applications provided.</li>
          <li><span className="text-[#D4AF37]">·</span> Actions or decisions made by insurance carriers.</li>
        </ul>
        <p className="mt-4">
          To the maximum extent permitted by law, Impact Legacy Group's liability is limited to the amount paid for our services, if any.
        </p>
      </>
    ),
  },
  {
    title: "Termination Of Services",
    body: (
      <>
        We reserve the right to terminate your access to our services at any time without notice if we believe you have violated these Terms and Conditions or engaged in unlawful activity.
      </>
    ),
  },
  {
    title: "Governing Law",
    body: (
      <>
        These Terms and Conditions are governed by the laws of the State of Texas, without regard to its conflict of law principles. Any disputes arising from these terms or your use of our services will be resolved in the courts of Texas.
      </>
    ),
  },
  {
    title: "Changes To Terms And Conditions",
    body: (
      <>
        Impact Legacy Group reserves the right to update or modify these Terms and Conditions at any time. Any changes will be posted on our website, and continued use of our services constitutes acceptance of those changes.
      </>
    ),
  },
];

export default function Terms() {
  return (
    <div data-testid="terms-page" className="pt-32 pb-32">
      <section className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="overline mb-6">— Terms & Conditions</div>
        <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tighter leading-[1] mb-6">
          Clear terms. <span className="italic text-[#D4AF37]">Honest service.</span>
        </h1>
        <div className="text-xs text-white/45 tracking-[0.22em] uppercase mb-8">Effective Date: {EFFECTIVE_DATE}</div>

        <p className="text-white/65 text-lg leading-relaxed mb-12">
          Welcome to Impact Legacy Group. By using our services, you agree to the following Terms and Conditions. Please read them carefully as they govern your use of our website, products, and services.
        </p>

        <div className="gold-line mb-12" />

        <div className="space-y-12">
          {sections.map((s, i) => (
            <div key={s.title} data-testid={`terms-section-${i}`}>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-serif text-2xl text-[#D4AF37]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-serif text-2xl md:text-3xl text-white leading-tight">{s.title}</h2>
              </div>
              <div className="text-white/70 leading-relaxed pl-10 space-y-2">{s.body}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 p-8 border border-[#D4AF37]/30 bg-[#1A1A1A]/60">
          <div className="flex items-baseline gap-4 mb-5">
            <span className="font-serif text-2xl text-[#D4AF37]">12</span>
            <h2 className="font-serif text-2xl md:text-3xl text-white leading-tight">Contact Us</h2>
          </div>
          <p className="text-white/70 leading-relaxed mb-6 pl-10">
            If you have any questions about these Terms and Conditions, please contact us:
          </p>
          <div className="pl-10 space-y-3">
            <div className="text-white font-medium">Impact Legacy Group</div>
            <a href="mailto:impactlegacy.davis@gmail.com" data-testid="terms-contact-email" className="flex items-center gap-3 text-white/75 hover:text-[#D4AF37] transition-colors">
              <Mail className="w-4 h-4 text-[#D4AF37]" /> impactlegacy.davis@gmail.com
            </a>
            <a href="tel:9364435365" data-testid="terms-contact-phone" className="flex items-center gap-3 text-white/75 hover:text-[#D4AF37] transition-colors">
              <Phone className="w-4 h-4 text-[#D4AF37]" /> 936-443-5365
            </a>
            <div className="flex items-start gap-3 text-white/75">
              <MapPin className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
              <span>12218 Medina Mill, San Antonio, TX 78253</span>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-6 text-sm">
          <Link data-testid="terms-to-privacy" to="/privacy" className="inline-flex items-center gap-2 text-[#D4AF37] uppercase tracking-[0.22em] text-xs border-b border-[#D4AF37]/40 pb-1 hover:gap-4 transition-all">
            Read Privacy Policy <ArrowRight className="w-4 h-4" />
          </Link>
          <Link data-testid="terms-to-contact" to="/contact" className="inline-flex items-center gap-2 text-white/70 uppercase tracking-[0.22em] text-xs border-b border-white/20 pb-1 hover:text-white hover:gap-4 transition-all">
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
